import { useState } from "react";
import { MdOutlineMailOutline, MdDomain } from "react-icons/md";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { RiArrowDropRightLine } from "react-icons/ri";
import { IoLocationOutline } from "react-icons/io5";
import MainLayout from "../../layouts/MainLayOut";

function PartnerLogo({ src, alt, to, fallbackSrc }) {
  const [imgSrc, setImgSrc] = useState(src);

  return (
    <img
      src={imgSrc}
      alt={alt}
      loading="lazy"
      className="h-10"
      onError={() =>
        setImgSrc(fallbackSrc || "https://via.placeholder.com/150?text=Logo")
      }
      style={{ cursor: to ? "pointer" : "default" }}
      onClick={() => {
        if (to) window.open(to, "_blank");
      }}
    />
  );
}

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    subject: "Follow-Up Request",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleForm = async (e) => {
    e.preventDefault();

    const { name, email, subject, message } = formData;
    if (!name || !email || !subject || !message) {
      toast.error("All fields are required.");
      return;
    }

    const uri = `${process.env.REACT_APP_NODE_SERVER}v1/connectsupport`;
    try {
      const response = await fetch(uri, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, subject, message }),
      });

      if (!response.ok) throw new Error("Network error");

      const data = await response.json();
      toast.success(data.message || "Support Request Sent");

      setFormData({
        name: "",
        email: "",
        subject: "Follow-Up Request",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to submit request");
      console.error("Error:", error);
    }
  };

  return (
    <MainLayout>
      <div className=" flex flex-col bg-gradient-to-br from-slate-800 to-slate-900 text-slate-100">
        <main className="flex flex-col md:flex-row max-w-7xl mx-auto px-6 py-3 md:px-20 md:py-10 gap-12 flex-grow">
          {/* Contact Info */}
          <section className="md:w-1/3 space-y-8 pr-6 md:border-r border-slate-700">
            <header className="text-slate-50 space-y-2">
              <h1 className="text-4xl font-extrabold">Contact Us</h1>
              <nav className="flex items-center gap-1 text-slate-400 text-sm">
                <Link to="/" className="hover:underline">
                  Home
                </Link>
                <RiArrowDropRightLine size={22} />
                <span>Contact Us</span>
              </nav>
            </header>

            <div className="space-y-4 pt-6">
              <div className="flex items-start gap-3">
                <IoLocationOutline className="text-fuchsia-500 w-6 h-6 mt-1 flex-shrink-0" />
                <address className="not-italic leading-relaxed text-base">
                  415 Krushal Commercial Complex, Above Westside, G M Road,
                  Chembur, Mumbai, India-400089
                </address>
              </div>

              <div className="flex items-center gap-3">
                <MdDomain className="text-fuchsia-500 w-6 h-6 flex-shrink-0" />
                <p className="font-medium">Token AI Labs Pvt. Ltd</p>
              </div>

              <div className="flex items-center gap-3">
                <MdOutlineMailOutline className="text-fuchsia-500 w-6 h-6 flex-shrink-0" />
                <a href="mailto:info@jaano.ai" className="hover:underline">
                  info@jaano.ai
                </a>
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="md:w-2/3 bg-slate-800 bg-opacity-70 rounded-xl p-8 shadow-xl">
            <form onSubmit={handleForm} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block mb-2 text-sm font-medium">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your name"
                    className="w-full p-3 rounded-lg text-slate-900 border border-slate-300 focus:ring-2 focus:ring-fuchsia-500 focus:outline-none transition"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block mb-2 text-sm font-medium">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email"
                    className="w-full p-3 rounded-lg text-slate-900 border border-slate-300 focus:ring-2 focus:ring-fuchsia-500 focus:outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block mb-2 text-sm font-medium">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Type your message here..."
                  className="w-full p-3 rounded-lg text-slate-900 border border-slate-300 focus:ring-2 focus:ring-fuchsia-500 focus:outline-none transition resize-none"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-fuchsia-600 hover:bg-fuchsia-700 rounded-lg font-semibold transition text-white tracking-wide">
                Send Message
              </button>
              {/* Contact SLA */}
              <div className="text-center text-sm text-slate-400 mt-6">
                <p>
                  📨 We respond to all messages within{" "}
                  <span className="text-fuchsia-400 font-medium">24 hours</span>
                  . Our support team is here to help!
                </p>
              </div>
            </form>
          </section>
        </main>
        <section className="text-slate-100 space-y-10 flex flex-col max-w-7xl mx-auto px-6 md:px-20  flex-grow">
          {/* Testimonials */}
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              What Our Customers Say
            </h2>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <blockquote className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
                <p className="text-base italic">
                  "Jaano.ai transformed how we support customers — faster
                  responses, better insights. Absolute game changer."
                </p>
                <footer className="mt-4 font-medium text-sm text-fuchsia-400">
                  —{" "}
                  <a
                    href="https://eduvert.co/"
                    className="hover:underline"
                    target="_blank"
                    rel="noopener noreferrer">
                    @Growth Head @ Eduvert
                  </a>
                </footer>
              </blockquote>
              <blockquote className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
                <p className="text-base italic">
                  "Their AI bots helped us scale support without scaling costs.
                  Setup was effortless, support was top-notch."
                </p>
                <footer className="mt-4 font-medium text-sm text-fuchsia-400">
                  —{" "}
                  <a
                    href="https://www.askdrshah.com/drshah.aspx"
                    className="hover:underline"
                    target="_blank"
                    rel="noopener noreferrer">
                    @CTO @ LifeForce
                  </a>
                </footer>
              </blockquote>
              <blockquote className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
                <p className="text-base italic">
                  "Thanks to Jaano.ai, our response time went from hours to
                  minutes. Our customers noticed — and loved it!"
                </p>
                <footer className="mt-4 font-medium text-sm text-fuchsia-400">
                  —{" "}
                  <a
                    href="https://viagemtechmates.com/"
                    className="hover:underline"
                    target="_blank"
                    rel="noopener noreferrer">
                    @Sales Manager @ ViagemTechmates
                  </a>
                </footer>
              </blockquote>
            </div>
          </div>

          {/* Partner Logos */}
          <div className="text-center pb-8">
            <h3 className="text-xl font-semibold mb-4">Trusted by Teams at</h3>
            <div className="flex flex-wrap justify-center gap-8 items-center opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-300">
              <PartnerLogo
                src="https://i0.wp.com/eduvert.co/wp-content/uploads/2024/06/Eduvert-HD-LOGO-png-reworked-2-e1726667619167.png"
                alt="Eduvert"
                fallbackSrc="https://via.placeholder.com/150?text=Eduvert"
                to="https://eduvert.co"
              />
              <PartnerLogo
                src="https://www.lifeforce.in/Images/logo.png"
                alt="Life Force Homeopathy"
                fallbackSrc="https://via.placeholder.com/150?text=LifeForce"
                to="https://www.lifeforce.in"
              />
              <PartnerLogo
                src="https://viagemtechmates.com/wp-content/uploads/2023/04/logo.gif"
                alt="Viagem Techmates"
                fallbackSrc="https://via.placeholder.com/150?text=Viagem"
                to="https://viagemtechmates.com"
              />
            </div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
}

export default Contact;
